import React, { useState, useEffect } from 'react';
import {
  Globe,
  Palette,
  UtensilsCrossed,
  Briefcase,
  Check,
  ArrowRight,
  ArrowLeft,
  Send,
  Plus
} from 'lucide-react';
import './Packages.css';

const addOns = [
  { id: 'social-media', name: 'Social Media Setup', price: 199, priceLabel: '+$199', type: 'one-time', description: 'Set up and brand your Facebook, Instagram & Google Business profiles.' },
  { id: 'business-email', name: 'Business Email (5 accounts)', price: 99, priceLabel: '+$99 setup + $15/mo', type: 'one-time', description: 'Professional email addresses (e.g. you@yourbusiness.com).' },
  { id: 'booking-system', name: 'Online Booking System', price: 299, priceLabel: '+$299', type: 'one-time', description: 'Let customers book appointments directly on your website.' },
  { id: 'seo-report', name: 'Monthly SEO Report', price: 99, priceLabel: '+$99/mo', type: 'monthly', description: 'Monthly report showing how many people found you on Google.' },
  { id: 'care-plan', name: 'Monthly Website Care Plan', price: 79, priceLabel: '+$79–$199/mo', type: 'monthly', description: 'We handle updates, backups, and security so you don\'t have to.' },
  { id: 'review-gen', name: 'Review Generation Setup', price: 149, priceLabel: '+$149', type: 'one-time', description: 'Automated system to ask happy customers for Google/Yelp reviews.' },
  { id: 'qr-menu', name: 'QR Code Menu', price: 99, priceLabel: '+$99', type: 'one-time', description: 'Scannable QR code for tables linking to your digital menu.' },
  { id: 'business-card', name: 'Business Card Design', price: 149, priceLabel: '+$149', type: 'one-time', description: 'Professional business card design matching your brand.' },
];

const valueBadges = {
  'online-presence': { marketValue: '$1,440', savings: '$841' },
  'brand-website': { marketValue: '$2,889', savings: '$1,590' },
  'restaurant': { marketValue: '$4,237', savings: '$2,638' },
  'full-suite': { marketValue: '$5,684', savings: '$3,185' },
};

const packages = [
  {
    id: 'online-presence',
    icon: Globe,
    name: 'Online Presence Pack',
    tagline: 'Just Get Me Online',
    description: 'Perfect for brand new businesses that need a professional website fast.',
    bestFor: 'Local tradespeople, sole traders, new small businesses',
    delivery: '5–7 days',
    gradient: 'linear-gradient(135deg, #2dd4bf, #06b6d4)',
    tiers: [
      { name: 'Basic', price: '$599', priceNum: 599, monthly: '$49/mo', detail: 'Template design, you provide content', features: ['Up to 3-4 page website', 'Mobile-friendly design', 'Contact form', 'Google Maps & hours', 'Basic Google setup', 'Social media links', '2 revision rounds'] },
      { name: 'Standard', price: '$849', priceNum: 849, monthly: '$49/mo', popular: true, detail: 'Semi-custom, we help write content', features: ['Everything in Basic', 'Semi-custom design', 'We help write content', 'Stock photos included', '5 revision rounds', 'Priority email + chat support', '10–14 day delivery'] },
      { name: 'Premium', price: '$999', priceNum: 999, monthly: '$49/mo', detail: 'Custom design, we write all content', features: ['Everything in Standard', '100% custom design', 'We write ALL content', 'Custom photography direction', 'Unlimited revisions', 'Phone + video call support', '14–21 day delivery'] },
    ],
  },
  {
    id: 'brand-website',
    icon: Palette,
    name: 'Brand + Website Pack',
    tagline: 'Look Professional',
    description: 'For growing businesses that need to compete visually with a full brand identity.',
    bestFor: 'Salons, gyms, consultants, contractors',
    delivery: '10–14 days',
    gradient: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    tiers: [
      { name: 'Basic', price: '$1,299', priceNum: 1299, monthly: '$79/mo', detail: 'Template + logo, you provide photos/content', features: ['Everything in Online Presence', 'Custom logo (3 concepts)', 'Brand color palette & fonts', 'Up to 6-page website', 'Photo gallery section', 'Services/pricing page', 'Google Business Profile setup', 'Basic SEO', '5 revision rounds'] },
      { name: 'Standard', price: '$1,499', priceNum: 1499, monthly: '$79/mo', popular: true, detail: 'Semi-custom, stock photos, we help with copy', features: ['Everything in Basic', 'Semi-custom design', 'Stock photos included', 'We help write copy', 'Priority support', '10–14 day delivery'] },
      { name: 'Premium', price: '$1,799', priceNum: 1799, monthly: '$79/mo', detail: 'Fully custom, we handle everything', features: ['Everything in Standard', '100% custom design', 'We write ALL content', 'Custom photography direction', 'Unlimited revisions', 'Phone + video call support'] },
    ],
  },
  {
    id: 'restaurant',
    icon: UtensilsCrossed,
    name: 'Restaurant Pro Pack',
    tagline: 'Restaurants & Cafes',
    description: 'Specialist package for food businesses with digital menus and reservations.',
    bestFor: 'Restaurants, cafes, bakeries, food trucks, bars',
    delivery: '14 days',
    gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    tiers: [
      { name: 'Basic', price: '$1,599', priceNum: 1599, monthly: '$99/mo', detail: 'Template menu, standard layout', features: ['Everything in Brand + Website', 'Beautiful digital menu', 'Reservation/booking form', 'Up to 8-page website', 'Food photo gallery', 'Hours, location & parking', 'Google & Yelp reviews', 'Instagram feed widget', 'Menu QR code for tables'] },
      { name: 'Standard', price: '$1,899', priceNum: 1899, monthly: '$99/mo', popular: true, detail: 'Custom design, online reservation form', features: ['Everything in Basic', 'Custom design', 'Online reservation system', 'Priority support', 'We write all copy'] },
      { name: 'Premium', price: '$2,199', priceNum: 2199, monthly: '$99/mo', detail: 'Fully custom, online ordering, Instagram feed', features: ['Everything in Standard', '100% custom design', 'Online ordering integration', 'Instagram feed integration', 'Unlimited revisions', 'Phone + video support'] },
    ],
  },
  {
    id: 'full-suite',
    icon: Briefcase,
    name: 'Full Business Suite',
    tagline: 'Launch My Business',
    description: 'Maximum value. Everything you need to launch or rebrand — done properly.',
    bestFor: 'Businesses launching or rebranding from scratch',
    delivery: '21 days',
    gradient: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
    tiers: [
      { name: 'Basic', price: '$2,499', priceNum: 2499, monthly: '$149/mo', detail: 'Template base, full feature set', features: ['Everything in Brand + Website', 'Full brand identity kit', 'Business card design', 'Up to 12-page website', 'Business email setup', 'Booking/scheduling system', 'Quote request form', 'Social media setup (3 platforms)', 'Advanced SEO', 'Unlimited revisions'] },
      { name: 'Standard', price: '$3,299', priceNum: 3299, monthly: '$149/mo', popular: true, detail: 'Semi-custom, we handle content & copy', features: ['Everything in Basic', 'Semi-custom design', 'We handle content & copy', 'Priority support'] },
      { name: 'Premium', price: '$4,499', priceNum: 4499, monthly: '$149/mo', detail: '100% custom, complete white-glove service', features: ['Everything in Standard', '100% custom design', 'Complete white-glove service', 'Phone + video support', 'Dedicated project manager'] },
    ],
  },
];

const Packages = ({ onCheckout, preSelectedPackageId }) => {
  const [step, setStep] = useState('packages');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  // Auto-select package from quiz recommendation
  useEffect(() => {
    if (preSelectedPackageId) {
      const pkg = packages.find(p => p.id === preSelectedPackageId);
      if (pkg) {
        setSelectedPackage(pkg);
        setStep('tiers');
      }
    }
  }, [preSelectedPackageId]);

  const scrollToTop = () => {
    setTimeout(() => {
      document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setStep('tiers');
    scrollToTop();
  };

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setSelectedAddOns([]);
    setStep('addons');
    scrollToTop();
  };

  const handleBack = () => {
    if (step === 'addons') { setStep('tiers'); setSelectedTier(null); setSelectedAddOns([]); }
    else if (step === 'tiers') { setStep('packages'); setSelectedPackage(null); }
    scrollToTop();
  };

  const toggleAddOn = (addon) => {
    setSelectedAddOns((prev) =>
      prev.find((a) => a.id === addon.id) ? prev.filter((a) => a.id !== addon.id) : [...prev, addon]
    );
  };

  const handleCheckout = () => {
    if (onCheckout) onCheckout(selectedPackage.name, { ...selectedTier, addOns: selectedAddOns });
  };

  const addOnsTotal = selectedAddOns.reduce((sum, a) => sum + a.price, 0);
  const depositAmount = selectedTier ? Math.round((selectedTier.priceNum + addOnsTotal) * 0.5) : 0;

  return (
    <section className="packages section" id="packages">
      <div className="packages__bg"><div className="packages__bg-gradient" /></div>

      <div className="container">
        <div className="packages__header">
          <span className="section-label">Packages & Pricing</span>
          <h2 className="section-title">Pick your package.<br />Choose your tier. Done.</h2>
          <p className="section-subtitle">Four packages designed for specific business types. Three tiers within each so you get exactly what you need.</p>
          <a href="#quiz" className="packages__help-link">Not sure which one's right for you? <strong>Help me choose</strong></a>
        </div>

        {/* Step indicators */}
        <div className="packages__steps-indicator">
          <div className={`packages__step-dot ${step === 'packages' || step === 'tiers' || step === 'addons' ? 'active' : ''}`}><span>1</span><p>Package</p></div>
          <div className="packages__step-line" />
          <div className={`packages__step-dot ${step === 'tiers' || step === 'addons' ? 'active' : ''}`}><span>2</span><p>Tier</p></div>
          <div className="packages__step-line" />
          <div className={`packages__step-dot ${step === 'addons' ? 'active' : ''}`}><span>3</span><p>Add-ons</p></div>
        </div>

        <div>
          {step === 'packages' && (
            <div key="packages-grid" className="packages__grid-wrapper packages__fade-in">
              <p className="packages__hint">👇 Tap a package to see what's included</p>
              <div className="packages__grid">
              {packages.map((pkg) => (
                <div key={pkg.id} className="packages__card" onClick={() => handlePackageSelect(pkg)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handlePackageSelect(pkg)} aria-label={`Select ${pkg.name}`}>
                  <div className="packages__value-badge">
                    <span className="packages__value-market"><s>{valueBadges[pkg.id].marketValue}</s> market value</span>
                  </div>
                  <div className="packages__card-icon" style={{ background: pkg.gradient }}><pkg.icon size={26} /></div>
                  <span className="packages__card-tagline">{pkg.tagline}</span>
                  <h3 className="packages__card-name">{pkg.name}</h3>
                  <p className="packages__card-desc">{pkg.description}</p>
                  <div className="packages__card-meta">
                    <span className="packages__card-price">From {pkg.tiers[0].price}</span>
                    <span className="packages__card-delivery">{pkg.delivery}</span>
                  </div>
                  <div className="packages__card-best">{pkg.bestFor}</div>
                  <div className="packages__card-action"><span>Select Package</span><ArrowRight size={16} /></div>
                </div>
              ))}
              </div>
            </div>
          )}

          {step === 'tiers' && selectedPackage && (
            <div key="tiers-view" className="packages__tiers packages__fade-in">
              <button className="packages__back" onClick={handleBack}><ArrowLeft size={16} /> Back to all packages</button>
              <div className="packages__tiers-header">
                <div className="packages__tiers-icon" style={{ background: selectedPackage.gradient }}><selectedPackage.icon size={22} /></div>
                <div><h3 className="packages__tiers-title">{selectedPackage.name}</h3><p className="packages__tiers-subtitle">{selectedPackage.description}</p></div>
              </div>
              <div className="packages__tiers-grid">
                {selectedPackage.tiers.map((tier) => (
                  <div key={tier.name} className={`packages__tier-card ${tier.popular ? 'packages__tier-card--popular' : ''}`}>
                    {tier.popular && <div className="packages__tier-badge">Most Popular</div>}
                    <h4 className="packages__tier-name">{tier.name}</h4>
                    <div className="packages__tier-price"><span className="packages__tier-amount">{tier.price}</span><span className="packages__tier-period">one-time</span></div>
                    <div className="packages__tier-monthly">+ {tier.monthly} hosting & support</div>
                    <p className="packages__tier-detail">{tier.detail}</p>
                    <ul className="packages__tier-features">{tier.features.map((f, i) => <li key={i}><Check size={15} /><span>{f}</span></li>)}</ul>
                    <button className={`packages__tier-btn ${tier.popular ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleTierSelect(tier)}>Select {tier.name}<ArrowRight size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'addons' && selectedTier && (
            <div key="addons-view" className="packages__addons packages__fade-in">
              <button className="packages__back" onClick={handleBack}><ArrowLeft size={16} /> Back to tiers</button>
              <div className="packages__addons-header">
                <h3 className="packages__addons-title">Enhance your project</h3>
                <p className="packages__addons-subtitle">Add extras to get even more from your website. These are optional — skip if you're happy with your selection.</p>
              </div>
              <div className="packages__addons-grid">
                {addOns.map((addon) => {
                  const isSelected = selectedAddOns.find((a) => a.id === addon.id);
                  return (
                    <div key={addon.id} className={`packages__addon-card ${isSelected ? 'packages__addon-card--selected' : ''}`} onClick={() => toggleAddOn(addon)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggleAddOn(addon)}>
                      <div className="packages__addon-check">{isSelected ? <Check size={16} /> : <Plus size={16} />}</div>
                      <div className="packages__addon-info"><h4>{addon.name}</h4><p>{addon.description}</p></div>
                      <div className="packages__addon-price">{addon.priceLabel}</div>
                    </div>
                  );
                })}
              </div>
              <div className="packages__addons-summary">
                <div className="packages__addons-summary-left">
                  <div className="packages__addons-summary-row"><span>{selectedPackage.name} — {selectedTier.name}</span><strong>{selectedTier.price}</strong></div>
                  {selectedAddOns.length > 0 && <div className="packages__addons-summary-row"><span>{selectedAddOns.length} add-on{selectedAddOns.length > 1 ? 's' : ''}</span><strong>+${addOnsTotal}</strong></div>}
                  <div className="packages__addons-summary-row packages__addons-summary-row--total"><span>50% Deposit</span><strong>${depositAmount}</strong></div>
                </div>
                <button className="btn-primary packages__addons-checkout" onClick={handleCheckout}><Send size={16} /> Submit Request to Our Team<ArrowRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Packages;
export { packages };
